import React, { useEffect, useState } from "react";
import MaterialTable from "material-table";
import DeleteIcon from "@material-ui/icons/Delete";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import CancelPresentationIcon from "@mui/icons-material/CancelPresentation";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { ThemeProvider } from "@mui/material";
import { getAllDeliveryRequest, normalizeDate } from "../../utilities";
import { Tooltip } from "@mui/material";
import { Link } from "react-router-dom";
import { icons } from "../Utilities/UtilitiesWD";
import { useAppMuiTheme } from "../../theme/MuiThemeBridge";
import { PageHeader, PageShell } from "../UI/Page";
import { EmptyState, StatTile } from "../UI/States";
import {
  Pill,
  mtComponents,
  mtHeaderStyle,
  mtRowStyle,
  mtSearchFieldStyle,
  mtStyle,
} from "./wdUi";

const RequestDataTable = () => {
  const defaultMaterialTheme = useAppMuiTheme();
  const [dataStore, setDataStore] = useState([]);
  const [flag, setFlag] = useState(false);

  useEffect(() => {
    getAllDeliveryRequest(setDataStore);
  }, []);

  useEffect(() => {
    getAllDeliveryRequest(setDataStore);
  }, [flag]);

  const deleteRequest = async (id) => {
    const data = {
      id: id,
    };

    const rawResponse = await fetch("/api/getAllRequests/delete", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
    const content = await rawResponse.json();
  };

  const requestCount = Array.isArray(dataStore) ? dataStore.length : 0;
  const earliestCount = Array.isArray(dataStore)
    ? dataStore.filter((row) => row?.earliestAvailable).length
    : 0;

  const DeleteRequestButton = ({ rowData }) => (
    <Tooltip title="Delete Request">
      <button
        type="button"
        aria-label="Delete Request"
        className="app-icon-btn text-rose-600 hover:text-rose-700 dark:text-rose-400 dark:hover:text-rose-300"
        onClick={() => {
          deleteRequest(rowData._id);
          setFlag(!flag);
        }}
      >
        <DeleteIcon fontSize="small" />
      </button>
    </Tooltip>
  );

  return (
    <PageShell size="2xl">
      <PageHeader
        icon={InboxOutlinedIcon}
        tone="indigo"
        eyebrow="Windows & Doors"
        title="Open Requests"
        description="Incoming delivery requests awaiting review."
      />
      <div className="mb-6 grid grid-cols-2 gap-3 sm:max-w-md">
        <StatTile label="Open requests" value={requestCount} />
        <StatTile
          label="Earliest available"
          value={earliestCount}
          tone={earliestCount > 0 ? "sky" : "default"}
          hint="Need a date scheduled"
        />
      </div>
      <div className="app-card overflow-hidden rounded-2xl border">
        <ThemeProvider theme={defaultMaterialTheme}>
          <MaterialTable
            style={mtStyle}
            columns={[
              {
                title: "Order#",
                field: "salesOrder",
                render: (rowData) => (
                  <span className="font-semibold tabular-nums">
                    {rowData.salesOrder}
                  </span>
                ),
              },
              {
                title: "From",
                field: "requestName",
              },
              {
                title: "Submit Date",
                field: "requestSubmitted",
                // Old records store "M/D/YYYY" strings, so sort by real date
                customSort: (a, b) =>
                  new Date(a.requestSubmitted) - new Date(b.requestSubmitted),
                render: (rowData) =>
                  rowData.requestSubmitted ? (
                    <span className="whitespace-nowrap tabular-nums">
                      {normalizeDate(rowData.requestSubmitted)}
                    </span>
                  ) : null,
              },
              {
                title: "Request Date",
                field: "requestDate",
                render: (rowData) => {
                  if (rowData["earliestAvailable"]) {
                    return <Pill tone="sky">Earliest Available</Pill>;
                  } else {
                    return <p className="tabular-nums">{rowData.requestDate}</p>;
                  }
                },
              },
              {
                title: "Customer",
                field: "customer",
              },
              {
                title: "Job",
                field: "job",
              },
              {
                title: "Action",
                field: "Action",
                render: (rowData) => {
                  if (rowData["earliestAvailable"]) {
                    return (
                      <div className="flex items-center gap-0.5">
                        <DeleteRequestButton rowData={rowData} />
                        <Tooltip title="Schedule Request">
                          <Link
                            to={`/schEarliest/${rowData._id}`}
                            aria-label="Schedule Request"
                            className="app-icon-btn text-sky-600 hover:text-sky-700 dark:text-sky-300 dark:hover:text-sky-200"
                          >
                            <CalendarMonthIcon fontSize="small" />
                          </Link>
                        </Tooltip>
                      </div>
                    );
                  } else {
                    return (
                      <div className="flex items-center gap-0.5">
                        <DeleteRequestButton rowData={rowData} />
                        <Tooltip title="Accept Request">
                          <Link
                            to={`/acceptRequest/${rowData._id}`}
                            aria-label="Accept Request"
                            className="app-icon-btn text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300"
                          >
                            <CheckBoxIcon fontSize="small" />
                          </Link>
                        </Tooltip>
                        <Tooltip title="Reject Request">
                          <Link
                            to={`/rejectRequest/${rowData._id}`}
                            aria-label="Reject Request"
                            className="app-icon-btn text-rose-600 hover:text-rose-700 dark:text-rose-400 dark:hover:text-rose-300"
                          >
                            <CancelPresentationIcon fontSize="small" />
                          </Link>
                        </Tooltip>
                      </div>
                    );
                  }
                },
              },
            ]}
            data={dataStore.slice().reverse()}
            title=""
            icons={icons}
            components={mtComponents}
            options={{
              pageSize: 10, // make initial page size
              emptyRowsWhenPaging: false, // To avoid of having empty rows
              pageSizeOptions: [5, 10, 15, 25],
              headerStyle: { search: true, ...mtHeaderStyle },
              searchFieldStyle: mtSearchFieldStyle,
              rowStyle: mtRowStyle,
            }}
            localization={{
              body: {
                emptyDataSourceMessage: (
                  <EmptyState
                    icon={TaskAltOutlinedIcon}
                    title="No Open Requests"
                    description={
                      <>
                        If you think this is a mistake please contact{" "}
                        <strong>Help Desk</strong>.
                      </>
                    }
                  />
                ),
              },
            }}
          />
        </ThemeProvider>
      </div>
    </PageShell>
  );
};

export default RequestDataTable;
